<template>
  <v-dialog
    v-model="isOpen"
    max-width="900px"
    content-class="custom-dialog"
  >
    <v-card class="pa-5 custom-dialog-card">
      <v-card-title>
        <span class="headline">
          {{ title }}
        </span>
      </v-card-title>

      <v-card-text>
        <v-container
          grid-list-md
          pa-0
        >
          <v-layout wrap>
            <v-flex xs12>
              <v-text-field
                v-model="updatedCompany.name"
                :label="$t('general.forms.name')"
                :rules="textInputRules"
                :hint="$t('general.forms.required')"
                class="custom-input"
              />
            </v-flex>
          </v-layout>
          <div class="subheading">
            {{ $t('general.forms.projects') }}
          </div>
          <ul>
            <li
              v-for="project in nonDeletedProjects"
              :key="project.id"
            >
              {{ project.name }}
              <v-btn
                icon
                @click="() => removeProject(project.name)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </li>
            <li
              v-for="project in newProjects"
              :key="project"
            >
              {{ project }}
              <v-btn
                icon
                @click="() => removeProject(project)"
              >
                <v-icon>delete</v-icon>
              </v-btn>
            </li>
            <li>
              <v-text-field
                v-model="newProjectName"
                :label="$t('general.forms.name')"
                :rules="textInputRules"
                :hint="$t('general.forms.required')"
                class="custom-input"
              />
              <v-btn
                @click="addProject"
              >
                Add
              </v-btn>
            </li>
          </ul>
        </v-container>
      </v-card-text>

      <v-card-actions>
        <v-btn
          color="blue darken-1"
          flat
          class="cancel"
          @click="close"
        >
          {{ $t('general.dialogs.cancel') }}
        </v-btn>

        <v-btn
          :disabled="!updatedCompany.name"
          color="blue darken-1"
          flat
          class="confirm"
          @click="handleSave"
        >
          {{ $t('general.dialogs.save') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import gql from 'graphql-tag';

export default {
  name: 'EditCompanyModal',
  components: {},
  props: {
    new: {
      type: Boolean,
      required: false,
    },
    storedCompany: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      textInputRules: [value => !!value || this.$t('general.forms.required')],
      newProjects: [],
      deletedProjects: [],
      newProjectName: '',
      updatedCompany: {
        name: '',
        projects: [],
      },
      isOpen: true,
    };
  },
  computed: {
    title() {
      if (this.new) return this.$t('general.admin.tabs.companies.new');
      return this.$t('general.admin.tabs.companies.edit');
    },
    nonDeletedProjects() {
      if (this.updatedCompany && this.updatedCompany.projects) {
        return this.updatedCompany.projects.filter(project => !this.deletedProjects.includes(project.id));
      }
      return [];
    },
  },
  watch: {
    isOpen(value) {
      if (!value) {
        this.close();
      }
    },
  },
  mounted() {
    if (this.storedCompany) {
      this.updatedCompany = this.storedCompany;
    }
  },
  methods: {
    close() {
      this.$emit('close');
    },
    handleSave() {
      if (this.new) {
        this.createCompany();
      } else {
        this.updateCompany();
      }
    },
    addProject() {
      if (this.updatedCompany.projects.map(project => project.name).includes(this.newProjectName) ||
        this.newProjects.includes(this.newProjectName)) {
        this.$root.vtoast.show({
          message: this.$t('general.dialogs.uniqueName'),
          color: 'warning',
        });
      } else {
        this.newProjects = [
          ...this.newProjects,
          this.newProjectName,
        ];
        this.newProjectName = '';
      }
    },
    removeProject(name) {
      if (this.newProjects.includes(name)) {
        this.newProjects = this.newProjects.filter(projectName => projectName !== name);
      } else {
        const matchingProject = this.updatedCompany.projects.find(project => project.name === name);
        if (matchingProject) {
          this.deletedProjects = [
            ...this.deletedProjects,
            matchingProject.id,
          ];
        }
      }
    },
    async syncProjects(companyId) {
      const deleteProjectPromises = this.deletedProjects.map(projectId => this.$apollo.mutate({
        mutation: gql`
          mutation($id: uuid!) {
            delete_project(where: {
              id: {
                _eq: $id
              }
            }) {
              affected_rows
            }
          }
        `,
        variables: {
          id: projectId,
        },
      }));
      await Promise.all(deleteProjectPromises);
      const newProjectPromises = this.newProjects.map(project => this.$apollo.mutate({
        mutation: gql`
          mutation($name: String!, $groupId: uuid!) {
            insert_project(objects: { name: $name, groupId: $groupId }) {
              returning {
                id
              }
            }
          }
        `,
        variables: {
          name: project,
          groupId: companyId,
        },
      }));
      await Promise.all(newProjectPromises);
    },
    async createCompany() {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation($name: String!) {
              insert_group(objects: { name: $name }) {
                returning {
                  id
                }
              }
            }
          `,
          variables: {
            name: this.updatedCompany.name,
          },
        });
        this.syncProjects(data.insert_group.returning[0].id);
        this.close();
      } catch (e) {
        this.$root.vtoast.show({
          message: `${this.$t('general.dialogs.error')}: ${e.message}`,
          color: 'error',
        });
      }
    },
    async updateCompany() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation($name: String!, $id: uuid!) {
              update_group(where: { id: { _eq: $id } }, _set: { name: $name }) {
                affected_rows
              }
            }
          `,
          variables: {
            id: this.updatedCompany.id,
            name: this.updatedCompany.name,
          },
        });
        this.syncProjects(this.updatedCompany.id);
        this.close();
      } catch (e) {
        this.$root.vtoast.show({
          message: `${this.$t('general.dialogs.error')}: ${e.message}`,
          color: 'error',
        });
      }
    },
  },
};
</script>
